import { useEffect } from "react";
import { PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroSection from "@/components/landing/HeroSection";
import TrustMetricsSection from "@/components/landing/TrustMetricsSection";
import ServicesSection from "@/components/landing/ServicesSection";
import AboutRebeccaSection from "@/components/landing/AboutRebeccaSection";
import CorporateProfileSection from "@/components/landing/CorporateProfileSection";
import RegionalCoverageSection from "@/components/landing/RegionalCoverageSection";
import PastEventsSection from "@/components/landing/PastEventsSection";
import PortfolioSection from "@/components/landing/PortfolioSection";
import FAQSection from "@/components/landing/FAQSection";
import SeoCopySection from "@/components/landing/SeoCopySection";
import ConsultationSection from "@/components/landing/ConsultationSection";
import { contactDetails, navigationLinks, socialLinks } from "@/data/eventOrganizerContent";

const EventOrganizerPage = () => {
  useEffect(() => {
    const seo = {
      title: "RJL Events HK | Asia Pacific Corporate Event Services",
      description:
        "RJL Events HK delivers Asia Pacific corporate event strategy and execution, including conference production, speaker acquisition, event management, and master of ceremony services.",
      keywords:
        "Asia Pacific corporate events, Hong Kong conference producer, corporate event management, conference production, speaker acquisition, master of ceremony",
      image:
        "https://images.unsplash.com/photo-1719432270124-5c1327c63747?auto=format&fit=crop&w=1800&q=85",
    };

    document.title = seo.title;

    const setMetaTag = (selector, attributeName, attributeValue, content) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const pageUrl = window.location.href;

    setMetaTag('meta[name="description"]', "name", "description", seo.description);
    setMetaTag('meta[name="keywords"]', "name", "keywords", seo.keywords);
    setMetaTag('meta[name="robots"]', "name", "robots", "index,follow");
    setMetaTag('meta[name="author"]', "name", "author", "RJL Events HK");

    setMetaTag('meta[property="og:type"]', "property", "og:type", "website");
    setMetaTag('meta[property="og:site_name"]', "property", "og:site_name", "RJL Events HK");
    setMetaTag('meta[property="og:title"]', "property", "og:title", seo.title);
    setMetaTag('meta[property="og:description"]', "property", "og:description", seo.description);
    setMetaTag('meta[property="og:url"]', "property", "og:url", pageUrl);
    setMetaTag('meta[property="og:image"]', "property", "og:image", seo.image);

    setMetaTag('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", seo.title);
    setMetaTag('meta[name="twitter:description"]', "name", "twitter:description", seo.description);
    setMetaTag('meta[name="twitter:image"]', "name", "twitter:image", seo.image);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", pageUrl);
  }, []);

  return (
    <div className="luxury-page min-h-screen bg-[#f8f6fb] text-stone-900" data-testid="event-organizer-page">
      <header className="nav-blur sticky top-0 z-50 border-b border-fuchsia-100/90 bg-[#f8f6fb]/90" data-testid="site-header">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-6 px-6 py-4 md:px-10">
          <a href="#home" data-testid="site-brand-link" className="text-xl tracking-wide text-violet-950 md:text-2xl">
            RJL Events HK
          </a>

          <nav className="hidden items-center gap-5 lg:flex" data-testid="site-navigation">
            {navigationLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid={link.testId}
                className="text-sm font-medium text-stone-700 transition-colors hover:text-fuchsia-800"
                aria-label={`Go to ${link.label} section`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <Button
            asChild
            className="h-10 rounded-full bg-fuchsia-700 px-5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-fuchsia-800"
            data-testid="header-call-now-button"
          >
            <a href={contactDetails.phoneHref} aria-label="Call now">
              <PhoneCall className="h-4 w-4" />
              Call Now
            </a>
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-7xl flex-col px-6 pb-20 pt-8 md:px-10">
        <HeroSection />
        <TrustMetricsSection />
        <ServicesSection />
        <AboutRebeccaSection />
        <CorporateProfileSection />
        <RegionalCoverageSection />
        <PastEventsSection />
        <PortfolioSection />
        <FAQSection />
        <SeoCopySection />
        <ConsultationSection />
      </main>

      <footer className="border-t border-violet-900/20 bg-violet-950 py-10" data-testid="site-footer">
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-5 px-6 md:px-10">
          <p data-testid="footer-brand-text" className="text-base text-fuchsia-100 md:text-lg">
            RJL Events HK · Pan-Asia Corporate Event Services, based in Hong Kong
          </p>
          <div className="flex flex-wrap items-center gap-4" data-testid="footer-social-links">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                data-testid={social.testId}
                className="text-sm text-stone-200 transition-colors hover:text-fuchsia-200"
                aria-label={`Visit ${social.label}`}
              >
                {social.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventOrganizerPage;